<script setup>
import { ref, computed } from 'vue'
import { interactions } from '../../../../config/interactions'
import { v4 as uuidv4 } from 'uuid'
import api from '@/services/axios' // ✅ ton axios centralisé
import CreateInteractionWizard from './CreateInteractionWizard.vue'

/**
 * Props
 * - context: 'default' | 'conditional' | 'nestedNode'
 * - testId:  _id Mongo du test (obligatoire si context='conditional')
 * - selectionMode: 'direct' | 'confirm' (tu utilises 'confirm')
 * - autoCreate: si true, la modale crée elle-même l'élément ET met à jour la ConditionalStructure.
 * - pageNodeId: ID du node parent (obligatoire si context='nestedNode')
 * - pageNodeData: Données du node parent (pour calcul position, etc.)
 */
const props = defineProps({
  context: { type: String, default: 'default' },    // 'default' | 'conditional' | 'nestedNode'
  testId:   { type: String, default: '' },
  selectionMode: { type: String, default: 'confirm' },
  isCatMode: { type: Boolean, default: false },     // conservé pour compat
  autoCreate: { type: Boolean, default: true },
  pageNodeId: { type: String, default: '' },        // pour nestedNode
  pageNodeData: { type: Object, default: () => ({}) }, // pour nestedNode
})

/**
 * Événements
 * - confirm(payload): toujours émis à la fin ; en mode conditional + autoCreate,
 *   payload contient { el_Type, elementId, label, node, persisted:true }.
 *   Le parent peut, si besoin, faire addNodes([node]) pour l'affichage immédiat.
 * - cancel(): fermeture
 */
const emit = defineEmits(['confirm','cancel'])

const reloadKey = ref(0)
const showModal = ref(false)
const isSubmitting = ref(false)

const isConfirmMode = computed(() => (props.selectionMode || 'direct').toLowerCase() === 'confirm')

// Liste des interactions (mêmes assets que chez toi)
const interactionTypes = computed(() => {
  const all = { ...interactions }
  if (props.isCatMode) {
    const { message, shortAnswer, ...cat } = all
    return cat
  }
  return all
})

function formatName(key) {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())
}

function getImagePath(key) {
  try { return new URL(`../../../../assets/${key}.png`, import.meta.url).href }
  catch { return new URL(`../../../../assets/default.png`, import.meta.url).href }
}

/* ------------------------- Helpers métier ------------------------- */

/** Types que tes slots Vue Flow savent rendre (node-*) */
function toRenderableType(t) {
  return (t === 'start' || t === 'question' || t === 'choice' || t === 'termination')
    ? t
    : 'question' // fallback visible (subtype stocké en data)
}

/** Construit un node prêt à persister côté ConditionalStructure */
function buildNodeForConditional({ el_Type, elementId, label }) {
  const renderType = toRenderableType(el_Type)

  const data = {
    elementId,
    label:
      label ??
      (renderType === 'start'
        ? 'Début'
        : renderType === 'termination'
        ? 'Fin'
        : 'Nouvelle question'),
    mode: renderType === 'question' ? 'options' : undefined,
  }

  // si l’interaction est un sous-type de question (ex: shortAnswer)
  if (renderType === 'question' && (el_Type !== 'question' && el_Type !== 'choice')) {
    data.subtype = el_Type
  }

  const node = {
    id: uuidv4(),
    type: renderType,
    position: {
      // position simple; tu peux calculer par viewport côté parent si besoin
      x: Math.random() * 400 + 100,
      y: Math.random() * 300 + 100,
    },
    collapsed: false,
    style: { width: 280, height: 140 },
    data,
  }

  return node
}

/** GET test complet (pour progression, ConditionalStructure fallback, etc.) */
async function getTest(testId) {
  const { data } = await api.get(`/tests/${testId}`)
  return data?.test || null
}

/** GET ConditionalStructure (via la route dédiée) */
async function getConditionalStructure(testId) {
  // La route renvoie { ConditionalStructure, testInfo: {...} }
  const { data } = await api.get(`/tests/${testId}/conditional-structure`)
  return data?.ConditionalStructure || {
    nodes: [],
    edges: [],
    startNode: null,
    terminationNodes: [],
    viewport: { x: 0, y: 0, zoom: 1 },
  }
}

/** PUT ConditionalStructure (merge contrôlé) */
async function putConditionalStructure(testId, structure, { replace = false } = {}) {
  const payload = {
    nodes: structure.nodes || [],
    edges: structure.edges || [],
    startNode: structure.startNode || null,
    terminationNodes: structure.terminationNodes || [],
    viewport: structure.viewport || { x: 0, y: 0, zoom: 1 },
    replace: !!replace,
  }
  await api.put(`/tests/${testId}/conditional-structure`, payload)
}

/** Assure qu’un test conditional a une structure initiale */
async function ensureConditionalStructure(testId) {
  const test = await getTest(testId)
  if (!test) return
  if (test.progression !== 'conditional') return

  const cs = test.ConditionalStructure
  const ok =
    cs &&
    Array.isArray(cs.nodes) &&
    Array.isArray(cs.edges) &&
    Array.isArray(cs.terminationNodes) &&
    cs.viewport &&
    typeof cs.viewport.x === 'number'

  if (ok) return
  await putConditionalStructure(testId, {
    nodes: [],
    edges: [],
    startNode: null,
    terminationNodes: [],
    viewport: { x: 0, y: 0, zoom: 1 },
  }, { replace: false })
}

/** POST: crée un élément pour le test et renvoie { elementId, element } */
async function createTestElementForInteraction({ testId, el_Type, el_Text }) {
  // Optionnel: baseModel depuis interactions[type]?.model() si tu veux options/scoring par défaut
  let baseModel = null
  if (interactions[el_Type]?.model) {
    try {
      const m = await interactions[el_Type].model()
      baseModel = JSON.parse(JSON.stringify(m))
    } catch {}
  }

  const payload = {
    el_Type,
    el_Text,
    options: baseModel?.options,
    scoring: baseModel?.scoring,
    difficulty_rating: baseModel?.difficulty_rating ?? 5,
    metadata: { ...(baseModel?.metadata || {}), source: 'CreateComponent' },
  }

  const { data } = await api.post(`/tests/${testId}/elements`, payload)
  const elementId = data?.el_ID
  if (!elementId) throw new Error('el_ID absent dans la reponse de creation d element')
  return { elementId, element: data?.element }
}

/** POST: crée un nested node dans une page et met à jour tests.pages + ConditionalStructure */
async function createNestedNodeInPage({ testId, pageNodeId, el_Type, el_Text, elementId, node }) {
  const payload = {
    pageNodeId,
    el_Type,
    el_Text,
    elementId,
    node
  }

  const { data } = await api.post(`/tests/${testId}/pages/nested-node`, payload)
  return data
}

/* ------------------------- Action principale ------------------------- */

const selectInteraction = async (type) => {
  const mode = (props.selectionMode || 'direct').toLowerCase()
  const label = formatName(type)

  // MODE CONFIRM (ton usage)
  if (mode === 'confirm') {

    // Contexte NESTED NODE → création d'un node enfant dans une page
    if (props.context === 'nestedNode') {
      console.log('🔵 [CreateComponent] nestedNode mode activated')
      console.log('🔵 [CreateComponent] testId:', props.testId)
      console.log('🔵 [CreateComponent] pageNodeId:', props.pageNodeId)
      console.log('🔵 [CreateComponent] type:', type)

      if (!props.testId || !props.pageNodeId) {
        console.error('[CreateComponent] testId et pageNodeId requis pour context=nestedNode')
        return
      }

      if (isSubmitting.value) return
      isSubmitting.value = true

      try {
        // 1) créer l'élément côté API
        console.log('🔵 [CreateComponent] Creating element...')
        const { elementId } = await createTestElementForInteraction({
          testId: props.testId,
          el_Type: type,
          el_Text: label,
        })
        console.log('🔵 [CreateComponent] Element created with ID:', elementId)

        // 2) construire le nested node
        const childNodes = props.pageNodeData?.children || []
        const order = childNodes.length
        const yPosition = 80 + order * 150

        const node = {
          id: uuidv4(),
          type: type, // type direct de l'interaction
          parentNode: props.pageNodeId,
          extent: 'parent',
          position: { x: 40, y: yPosition },
          data: {
            elementId,
            label,
            subtype: type,
            order,
            mode: 'options',
          },
          style: {
            width: 260,
            height: 120,
            background: '#fff',
            border: '1px solid #ced4da',
            borderRadius: '6px',
          },
        }
        console.log('🔵 [CreateComponent] Node constructed:', node)

        // 3) appel API pour ajouter dans tests.pages ET ConditionalStructure
        console.log('🔵 [CreateComponent] Calling API to add nested node...')
        const result = await createNestedNodeInPage({
          testId: props.testId,
          pageNodeId: props.pageNodeId,
          el_Type: type,
          el_Text: label,
          elementId,
          node
        })
        console.log('🔵 [CreateComponent] API response:', result)

        // 4) émettre vers le parent
        const payload = {
          el_Type: type,
          elementId,
          label,
          node,
          persisted: true,
        }
        console.log('🔵 [CreateComponent] Emitting confirm with payload:', payload)
        emit('confirm', payload)
      } catch (err) {
        console.error('[CreateComponent] nestedNode creation error:', err?.response?.data || err)
        emit('confirm', { el_Type: type, create: true, label, error: true })
      } finally {
        isSubmitting.value = false
      }

      return
    }

    // Contexte CONDITIONAL ET autoCreate → tout est géré ici
    if (props.context === 'conditional' && props.autoCreate) {
      if (!props.testId) {
        console.error('[CreateComponent] testId manquant pour context=conditional')
        return
      }

      if (isSubmitting.value) return
      isSubmitting.value = true

      try {
        // 1) structure conditionnelle prête
        await ensureConditionalStructure(props.testId)

        // 2) créer l’élément côté API
        const { elementId } = await createTestElementForInteraction({
          testId: props.testId,
          el_Type: type,
          el_Text: label,
        })

        // 3) construire le node et le pousser dans la ConditionalStructure
        const node = buildNodeForConditional({ el_Type: type, elementId, label })

        // lire la structure actuelle (idempotent + merge)
        const structure = await getConditionalStructure(props.testId)
        const merged = {
          ...structure,
          nodes: [...(structure.nodes || []), node],
          edges: [...(structure.edges || [])],
          startNode: structure.startNode || null,
          terminationNodes: [...(structure.terminationNodes || [])],
          viewport: structure.viewport || { x: 0, y: 0, zoom: 1 },
        }

        // 4) persister la structure
        await putConditionalStructure(props.testId, merged, { replace: true })

        // 5) émettre vers le parent un résultat “déjà persisté”
        //    → parent peut (optionnel) faire addNodes([node]) pour l’afficher sans reload
        emit('confirm', {
          el_Type: type,
          elementId,
          label,
          node,
          persisted: true,
        })
      } catch (err) {
        console.error('[CreateComponent] conditional autoCreate error:', err?.response?.data || err)
        // fallback de sécurité: le parent peut reprendre la main si besoin
        emit('confirm', { el_Type: type, create: true, label, error: true })
      } finally {
        isSubmitting.value = false
      }

      return
    }

    // MODE CONFIRM par défaut (non-conditional ou autoCreate=false)
    emit('confirm', { el_Type: type, create: true, label })
    return
  }

  // MODE DIRECT (historique) — tu peux le garder pour compat
  emit('confirm', { el_Type: type, create: true, label })
}

/* ------------------------- Dev scaffold modal handlers (inchangé) ------------------------- */

const handleInteractionCreated = async (data) => {
  // The interaction has already been generated by the wizard via /api/generateInteraction
  // Just show success message and refresh the list
  try {
    alert(`✅ Interaction "${data.interactionName}" created successfully!\n\nFiles generated:\n${data.files?.join('\n') || 'All files generated'}`)
    reloadKey.value++
    showModal.value = false
  } catch (err) {
    alert(`❌ Failed to process interaction: ${err.message}`)
  }
}
</script>

<template>
  <div>
    <h3>Choose an interaction to create a new element</h3>

    <div class="d-flex flex-wrap gap-3 justify-content-start">
      <button
        v-for="(interaction, key) in interactionTypes"
        :key="`${key}-${reloadKey}`"
        type="button"
        class="card border-0 shadow-sm p-3 clickable-card"
        :disabled="isSubmitting"
        style="cursor: pointer; width: 180px;"
        @click="selectInteraction(key)"
      >
        <div class="text-center">
          <img
            :src="getImagePath(key)"
            :alt="`${key} interaction image`"
            class="img-fluid rounded shadow-sm mb-2"
            :title="interaction.description || key"
          />
          <h5 class="card-title mb-1 text-capitalize">{{ key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()) }}</h5>
        </div>
      </button>
    </div>

    <hr />

    <div class="d-flex flex-wrap gap-3 justify-content-start">
      <button class="card border-0 shadow-sm p-3 clickable-card" @click="showModal = true">
        <img width="60" src="../../../../assets/toolbox.png" alt="toolbox image"
             class="img-fluid rounded shadow-sm mb-2 m-auto" title="Create your own interaction" />
        Create a new interaction <br />(developer tool)
      </button>
    </div>

    <CreateInteractionWizard v-if="showModal" @close="showModal = false" @created="handleInteractionCreated" />
  </div>
</template>

<style scoped>
.clickable-card:hover {
  transform: scale(1.03);
  transition: transform 0.2s ease-in-out;
}
.card .text-center { 
  margin: auto;
 }

 .card .text-center img{
  width: 90px!important;
 }
</style>
